// libraries
import React from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Typography, Stack, Divider } from '@mui/material'

// generic components
import Input from 'components/generic/form/Input'

// helpers and utils
import { setDecimalPrecision } from 'utils/helpers'

const BillFooter = ({
  disabled,
  control,
  errors,
  totalGross,
  totalTare,
  totalNet,
  subtotalValue,
  taxValue,
  totalValue,
  pricePrepend,
}) => {
  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { showWeightDecimals } = settings?.purchaseSettings?.inboundTickets || {}

  const footerSummaryItems = [
    {
      label: 'Gross:',
      value: `${setDecimalPrecision(totalGross, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      })} ${defaultYardUm}`,
    },
    {
      label: 'Tare:',
      value: `${setDecimalPrecision(totalTare, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      })} ${defaultYardUm}`,
    },
    {
      label: 'Net:',
      value: `${setDecimalPrecision(totalNet, {
        removeTrailingZeros: true,
        decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
      })} ${defaultYardUm}`,
    },
    {
      label: 'Subtotal:',
      value: `${pricePrepend}${setDecimalPrecision(subtotalValue, { decimalPrecisionOverride: 2 })}`,
    },
    {
      label: 'Tax:',
      value: `${pricePrepend}${setDecimalPrecision(taxValue, { decimalPrecisionOverride: 2 })}`,
    },
    {
      label: 'Total:',
      value: `${pricePrepend}${setDecimalPrecision(totalValue, { decimalPrecisionOverride: 2 })}`,
    },
  ]

  return (
    <Stack bottom={0} position="sticky" width="100%" sx={{ backgroundColor: 'background.default' }}>
      <Divider flexItem />
      <Stack direction="row" p={2}>
        <Input
          name="details.notes"
          label="Bill Notes"
          data-test="details-notes"
          variant="outlined"
          defaultValue=""
          control={control}
          disabled={disabled}
          error={!!errors.details?.notes}
          helperText={errors.details?.notes?.message}
        />
        {footerSummaryItems
          .filter(item => (taxValue === 0 ? ['Subtotal:', 'Tax:'].indexOf(item.label) === -1 : true))
          .map(item => (
            <Stack px={3} py={1} key={item.label}>
              <Typography color="textSecondary">{item.label}</Typography>
              <Typography align="left" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                {item.value}
              </Typography>
            </Stack>
          ))}
      </Stack>
    </Stack>
  )
}

BillFooter.propTypes = {
  disabled: PropTypes.bool,
  control: PropTypes.object,
  errors: PropTypes.object,
  totalGross: PropTypes.number,
  totalTare: PropTypes.number,
  totalNet: PropTypes.number,
  subtotalValue: PropTypes.number,
  taxValue: PropTypes.number,
  totalValue: PropTypes.number,
  pricePrepend: PropTypes.string,
}

BillFooter.defaultProps = {
  disabled: false,
  control: {},
  errors: {},
  totalGross: 0,
  totalTare: 0,
  totalNet: 0,
  subtotalValue: 0,
  taxValue: 0,
  totalValue: 0,
  pricePrepend: '$',
}

export default BillFooter
